import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ColumnSchema } from '../lib/api';
import { SchemaEditor } from '../components/SchemaEditor';
import { parseCsvHeader } from '../lib/csv';
import { buildSchemaFromHeaders } from '../lib/smart-mapping';
import type { useAuth } from '../auth/useAuth';

type Auth = ReturnType<typeof useAuth>;

export function TemplateEditorPage({
  mode,
}: {
  /** Reserved for future per-role gating in the editor itself; server still
   *  enforces RBAC on save/delete via _lib/rbac.ts. */
  auth: Auth;
  mode: 'create' | 'edit';
}) {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schema, setSchema] = useState<ColumnSchema[]>([]);
  const [busy, setBusy] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [headerInput, setHeaderInput] = useState('');
  const [showHeaderPaste, setShowHeaderPaste] = useState(false);

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    setBusy(true);
    api.getTemplate(Number(id))
      .then((r) => {
        setName(r.template.name);
        setDescription(r.template.description ?? '');
        setSchema(r.template.schema);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }, [mode, id]);

  const handlePasteHeader = () => {
    const lines = headerInput.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    let headers: string[] = [];
    if (lines.length === 1) {
      headers = parseCsvHeader(lines[0]);
    } else {
      headers = lines;
    }
    if (headers.length === 0) {
      setError('No headers detected.');
      return;
    }
    setSchema(buildSchemaFromHeaders(headers));
    setShowHeaderPaste(false);
    setHeaderInput('');
    setInfo(`Loaded ${headers.length} columns with smart Mockaroo-type guesses.`);
  };

  const handleSave = async () => {
    setError(null);
    setInfo(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (schema.length === 0) {
      setError('Add at least one column.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'create') {
        const r = await api.createTemplate({ name, description, schema });
        navigate(`/templates/${r.template.id}/edit`);
        setInfo('Saved.');
      } else {
        await api.updateTemplate(Number(id), { name, description, schema });
        setInfo('Saved.');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <header className="row">
        <div>
          <h1 style={{ marginBottom: '0.2em' }}>
            {mode === 'create' ? 'New template' : 'Edit template'}
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            Define the columns and Mockaroo field types. Saved templates are shared across the team.
          </p>
        </div>
        <span className="spacer" />
        <button type="button" onClick={() => navigate('/templates')}>Cancel</button>
        <button type="button" className="primary" onClick={handleSave} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </header>

      {error && <div className="banner error">{error}</div>}
      {info && <div className="banner ok">{info}</div>}

      <section className="card stack">
        <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
          <label style={{ flex: '1 1 240px' }}>
            <div className="muted" style={{ marginBottom: 4, fontSize: '0.85em' }}>Name</div>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sites — APAC variant" />
          </label>
          <label style={{ flex: '2 1 420px' }}>
            <div className="muted" style={{ marginBottom: 4, fontSize: '0.85em' }}>Description (optional)</div>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this template for? Any caveats?"
            />
          </label>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="button" onClick={() => setShowHeaderPaste((v) => !v)}>
              {showHeaderPaste ? 'Cancel paste' : 'Paste CSV header…'}
            </button>
          </div>
        </div>

        {showHeaderPaste && (
          <div className="stack" style={{ gap: 6 }}>
            <textarea
              placeholder={'Paste a CSV header row (commas) or one column per line.'}
              value={headerInput}
              onChange={(e) => setHeaderInput(e.target.value)}
              rows={4}
            />
            <div className="row">
              <button type="button" className="primary" onClick={handlePasteHeader} disabled={!headerInput.trim()}>
                Apply smart mapping
              </button>
              <span className="dim">Replaces the current schema below.</span>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <SchemaEditor schema={schema} onChange={setSchema} />
      </section>
    </div>
  );
}
